import React, { useCallback, useState } from 'react';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { getReadReceipts } from '../../../modules/Feeds/services/Feed.service';
import moment from 'moment';
import { DATETIME_FORMAT } from '../../../modules/Feeds/mappers/feedUtility';

/**
 * Hook for managing message menu options and read receipts modal
 * 
 * @returns {Object} Returns menu props object and read receipts modal props
 * @returns {Object} returns.menuProps - Complete menu props object with IVerticalMenuProps structure
 * @returns {Object} returns.readReceiptsModal - Modal props for read receipts modal
 */
export const useFeedMessagesMenuHook = () => {
  const [readReceiptsModal, setReadReceiptsModal] = useState({
    open: false,
    heading: 'Read Receipts',
    badgeLabel: '0',
    members: [],
  });

  /**
   * Handler for read receipts menu option
   * Fetches read receipts data and opens the modal
   */
  const handleReadReceipts = useCallback(async (messageId) => {
    if (!messageId) {
      console.warn('[useFeedMessagesMenuHook] Cannot fetch read receipts: messageId is missing');
      return;
    }

    try {
      const response = await getReadReceipts(messageId);
      const receiptsData = response?.data || [];
      
      const fullNames = receiptsData
        .filter((receipt) => receipt?.fullName != null && receipt?.fullName !== '')
        .map((receipt) => `${receipt.fullName} (${moment(receipt?.readAt).format(DATETIME_FORMAT)})`)
        .filter((name) => name.length > 0);
      
      const hasReceipts = fullNames.length > 0;
      
      setReadReceiptsModal({
        open: true,
        heading: hasReceipts ? 'Read Receipts' : 'No Read receipts',
        badgeLabel: hasReceipts ? fullNames.length.toString() : '0',
        members: hasReceipts ? fullNames : ['No Read Receipts'],
      });
    } catch (error) {
      console.error('[useFeedMessagesMenuHook] Failed to fetch read receipts:', error);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setReadReceiptsModal((prev) => ({ ...prev, open: false }));
  }, []);

  // Return complete menuProps object with IVerticalMenuProps structure
  const menuProps = {
    options: [
      {
        text: 'Read Receipts',
        onClick: handleReadReceipts, // This will be wrapped with messageId in transformation
        icon: React.createElement(DoneAllIcon, { 
          fontSize: 'small',
        }),
      },
    ],
    // Override menu position to always open to the right side
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
  };

  return {
    menuProps,
    readReceiptsModal: {
      ...readReceiptsModal,
      onClose: handleCloseModal,
    },
  };
};

